/* eslint-disable max-len */
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Pin, Send, Trash2 } from 'lucide-react';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { formatDurationToHrsAndMins } from '../../utils/helper';
import { RootState } from '../../store/reducers';

type Props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  metrics?: { [key: string]: any };
};

type Message = {
  content: string;
  id: string;
  role: 'bot' | 'user';
  pinned?: boolean;
  isGraph?: boolean;
};

type PinnedInsight = {
  id: string;
  question: string;
  answer: string;
  isGraph?: boolean;
};

const messagesP: Message[] = [
  // {
  //   role: 'bot',
  //   content:
  //     "Hello! I'm your AI bot. How can I help you analyze recent customer conversations and sales performance?",
  //   id: Date.now().toString(),
  //   pinned: false,
  // },
];

const colorPalette = [
  '#e4fff6',
  '#e3ffd7',
  '#ddc2ff',
  '#cdd3ff',
  '#ffdcf1',
];

const InsightChatModal = ({ open, setOpen, metrics }: Props) => {
  const [messages, setMessages] = useState<Message[]>(messagesP || []);
  const [input, setInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pinnedInsights, setPinnedInsights] = useState<PinnedInsight[]>([]);

  const loaderRef = useRef<HTMLDivElement | null>(null);

  const { user, selectedWorkspace } = useSelector((state: RootState) => state.common);

  const postInput = async (value: string) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        'https://jrq66lutssrdsdxe32ynvcpwfe0jkpqi.lambda-url.ap-south-1.on.aws',
        { query: value, org_id: user?.organisation_id, workspace_id: selectedWorkspace?.[0]?.id },
      );
      if (response?.data?.is_graph) {
        return JSON.parse(response?.data?.message);
      } else {
        return response?.data?.message
      }
    } catch (error) {
      console.log(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const focusOnLast = (pMessages: Message[]) => {
    const id = pMessages?.[pMessages?.length - 1]?.id;
    const elm = document.getElementById(id);
    if (elm) {
      elm.scrollIntoView({ block: 'nearest' });
    }
  };

  const handleSend = async () => {
    const val = input.trim();
    if (!val) return;
    let payload: Message = {
      content: val,
      id: Date.now().toString(),
      role: 'user',
    };
    let messageCopy = [...messages, payload];
    setMessages(messageCopy);
    setTimeout(() => {
      focusOnLast(messageCopy);
    }, 100);

    setInput('');

    const res: any = await postInput(val);
    if (res) {
      const isGraph = res?.data?.length;
      payload = {
        content: isGraph ? JSON.stringify(res?.data.map((itm, idx) => ({...itm, fill: colorPalette[idx % 5]}))) : res,
        id: Date.now().toString(),
        role: 'bot',
        ...(isGraph ? { isGraph } : {}),
      };
      messageCopy = [...messageCopy, payload];
      setMessages(messageCopy);
    }
    setTimeout(() => {
      focusOnLast(messageCopy);
    }, 100);
  };

  const handlePin = (message: Message) => {
    if (message.role === 'bot') {
      const lastUserMessage = messages
        .slice()
        .reverse()
        .find(m => m.role === 'user' && m.id < message.id);
      if (lastUserMessage) {
        const newPinnedInsight: PinnedInsight = {
          id: message.id,
          question: lastUserMessage?.content,
          answer: message?.content || '',
          isGraph: message?.isGraph
        };
        const insights = [...pinnedInsights, newPinnedInsight];
        setPinnedInsights(insights);
        localStorage.setItem('pinnedInsights', JSON.stringify(insights));
      }
    }
    setMessages(prev =>
      prev.map(m => (m.id === message.id ? { ...m, pinned: true } : m)),
    );
  };

  const handleUnpin = (id: string) => {
    const insights = pinnedInsights.filter(insight => insight.id !== id);
    setPinnedInsights(insights);
    setMessages(prev =>
      prev.map(m => (m.id === id ? { ...m, pinned: false } : m)),
    );
    localStorage.setItem('pinnedInsights', JSON.stringify(insights));
  };

  useEffect(() => {
    if (isLoading && loaderRef && loaderRef.current) {
      loaderRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isLoading]);

  useEffect(() => {
    if (window && window.localStorage) {
      const insights = localStorage.getItem('pinnedInsights');
      if (insights) {
        const parsedOpt = JSON.parse(insights);
        if (parsedOpt && parsedOpt?.length) {
          setPinnedInsights(parsedOpt);
        }
      }
    }
  }, []);

  useEffect(() => {
    setTimeout(async () => {
      let payload: Message = {
        content: 'START',
        id: Date.now().toString(),
        role: 'user',
      };
      const res: any = await postInput('START');
      if (res) {
        const isGraph = res?.data?.length;
        payload = {
          content: isGraph ? JSON.stringify(res?.data.map((itm, idx) => ({...itm, fill: colorPalette[idx % 5]}))) : res,
          id: Date.now().toString(),
          role: 'bot',
          ...(isGraph ? { isGraph } : {}),
        };
        setMessages([payload]);
      }
    }, 300)
  }, [])

  const averageConversationDuration = (metrics?.total_duration || 0) / (metrics?.total_conversation_count || 0);

  const formattedAvgDuration = formatDurationToHrsAndMins(averageConversationDuration || 0);
  const duration = formatDurationToHrsAndMins(metrics?.total_duration || 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-[90%] min-h-[230px] md:max-w-[80%] pt-6 pb-8 px-8 bg-white gap-0"
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogHeader className="space-y-0">
          <DialogTitle className="text-xl font-bold leading-7">
            Customer Insights Dashboard
          </DialogTitle>
        </DialogHeader>
        <div className="bg-gray-100 w-full h-full max-h-[600px] my-6 flex flex-col mx-auto gap-6 p-6 overflow-y-auto border-2 border-gray-200 rounded-xl">
          <div className="flex items-center gap-6 min-h-[500px]">
            <div className="w-[75%] bg-white p-5 h-full rounded-lg border shadow-sm border-gray-100 flex flex-col">
              <div className="text-lg font-bold leading-7 mb-6">
                Chat Interface
              </div>
              <div className="overflow-y-auto px-[5px] flex-1 flex flex-col">
                {messages.map((message, idx) => {
                  if (message.content === 'START') return [];
                  return (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      } mb-4`}
                      id={message.id}
                    >
                      <div
                        className={`rounded-lg p-4 max-w-[70%] font-normal flex group relative ${
                          message.role === 'user'
                            ? 'bg-secondary text-white'
                            : 'bg-gray-200'
                        } ${message?.isGraph ? 'w-full' : 'whitespace-pre-wrap'}`}
                      >
                        {message?.isGraph ? (
                          <GraphMessage data={JSON.parse(message?.content || "{}")}/>
                        ) : (
                          <>
                            {message?.content}
                          </>
                        )}
                        {idx !== 0 &&
                          message.role === 'bot' &&
                          !message.pinned && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePin(message)}
                              className="hidden group-hover:inline absolute top-0 right-0"
                            >
                              <Pin className="h-4 w-4" />
                              <span className="sr-only">
                                Pin this insight
                              </span>
                            </Button>
                          )}
                      </div>
                    </div>
                  )
                })}
                {isLoading && (
                  <div
                    ref={loaderRef}
                    className="flex items-center gap-2 text-sm mt-auto mb-0.5"
                  >
                    AI bot is responding
                    <div className="typing-loader" />
                  </div>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <input
                  type="text"
                  className="w-full rounded-lg border-[1.4px] border-[#D7D7D7] 
                  focus:border-secondary focus:outline-none focus:ring-0 text-base
                  p-3 py-2 font-normal"
                  onKeyPress={e =>
                    e.key === 'Enter' && !isLoading && handleSend()
                  }
                  value={input}
                  onChange={event => setInput(event.target.value)}
                />
                <Button
                  variant="secondary"
                  onClick={handleSend}
                  disabled={isLoading}
                >
                  <Send className="w-4.5 h-4.5 text-white" />
                </Button>
              </div>
            </div>
            <div className="flex flex-col w-[25%] h-full gap-3">
              <div className="w-full rounded-lg shadow-sm border border-gray-100 flex-1 bg-[#E1EBEF] p-3 flex flex-col gap-2">
                <p className='font-semibold text-lg'>Total conversations</p>
                <p className='mt-2.5 text-sm'><span className='font-semibold text-lg'>{metrics?.total_conversation_count || 0}</span> conversations</p>
                <p className='font-semibold text-lg'>{duration}</p>
                <p className='text-sm'><span className='font-semibold text-lg'>{metrics?.current_week_conversation_count || 0}</span> this week</p>
                <p className='text-sm'>Avg. <span className='font-semibold text-lg'>{formattedAvgDuration || 0}</span></p>
              </div>
              <div className="w-full rounded-lg shadow-sm border border-gray-100 flex-1 bg-[#E1EBEF] p-3 flex flex-col justify-around">
                <p className='font-semibold text-base'>Detailed conversations: <span className='text-xl font-bold'>{Math.round(Number(metrics?.detailed_conversations || 0))}%</span></p>
                <p className='font-semibold text-base'>High Intent Contacts: <span className='text-xl font-bold'>{Math.round(Number(metrics?.high_intent_contacts || 0))}%</span></p>
              </div>
              <div className="w-full rounded-lg shadow-sm border border-gray-100 flex-1 bg-[#E1EBEF] p-3 flex flex-col justify-around">
                <p className='font-semibold text-base'>
                  Talk-to-listen Ratio:{" "}
                  <span className='text-xl font-bold'>
                    {!(metrics?.salesperson_involvement === 0 || metrics?.customer_involvement === 0) ? (
                      `${Number(metrics?.salesperson_involvement)?.toFixed(0) || 0}:${Number(metrics?.customer_involvement)?.toFixed(0) || 0}`
                    ) : (
                      '0:0'
                    )}
                  </span>
                </p>
                <p className='font-semibold text-base'>Call Quality Score: <span className='text-xl font-bold'>{Number(metrics?.pitch_audit_score_latest) ? `${Math.round(Number(metrics?.pitch_audit_score_latest || 0))}%` : '-'}</span></p>
              </div>
            </div>
          </div>
          <div className="w-full rounded-lg shadow-sm border border-gray-100 flex-1 bg-white h-full p-6">
            {pinnedInsights.length === 0 ? (
              <p className="text-left font-semibold">
                No insights pinned yet. Pin a response from the chat to see it
                here.
              </p>
            ) : (
              <>
                <p className="text-left font-bold mb-2">Pinned Insights</p>
                <div className='max-h-[400px] overflow-y-auto'>
                  {pinnedInsights.map(insight => (
                    <div
                      key={insight.id}
                      className='mb-4 p-4 bg-white rounded-lg shadow'
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-bold">{insight.question}</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnpin(insight?.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Unpin this insight</span>
                        </Button>
                      </div>
                      <div className={`${insight?.isGraph ? 'max-w-[80%]' : ''}`}>
                        {insight?.isGraph ? (
                          <GraphMessage data={JSON.parse(insight?.answer)}/>
                        ) : (
                          <p className="text-sm text-gray-600">{insight?.answer}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GraphMessage = ({ data }: { data: { [key: string]: any }[] }) => {
  return (
    <div className="h-[230px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          {/* <CartesianGrid strokeDasharray="3 3" /> */}
          <XAxis dataKey="name" />
          {/* <YAxis /> */}
          <Tooltip />
          <Bar dataKey="value" radius={6}>
            {data.map(entry => (
              <Cell key={`cell-${entry?.id}`} fill={entry?.fill}/>
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default InsightChatModal;
